import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button } from '@fly/core';
import { Token } from '@fly/shared';
import { useDeleteTokenMutation } from '@fly/store';
import {
  ColumnDef,
  createColumnHelper,
  sortingFns,
} from '@tanstack/react-table';
import moment from 'moment';

import { useRevokeTokenDialog } from './dialogs';

interface TokensColumnsDependencies {
  FlyTextOverflow: React.ComponentType<any>;
  Search: React.ComponentType<{ text: string }>;
}

function isUnlimitedExpiration(expiry: Date): boolean {
  return isNaN(expiry.valueOf());
}

function getExpiryTime(expiry: Date): number {
  return isUnlimitedExpiration(expiry) ? Infinity : expiry.valueOf();
}

function canRevokeToken(
  expiry: Date,
  isAdmin: boolean,
  issuer: string,
  subject: string
) {
  const isCreatedByCurrentUser = issuer === subject;
  const isActive =
    isUnlimitedExpiration(expiry) || expiry.valueOf() > Date.now();

  return isActive && (isAdmin || isCreatedByCurrentUser);
}

const columnHelper = createColumnHelper<Token>();

export function useTokensColumnsTanstack(
  isAdmin: boolean,
  components: TokensColumnsDependencies
) {
  const { t } = useTranslation();
  const [deleteToken] = useDeleteTokenMutation();

  const { openRevokeTokenDialog } = useRevokeTokenDialog();
  const { FlyTextOverflow, Search } = components;

  const handleRevokeToken = useCallback(
    (tokenName: string, id: string) => {
      openRevokeTokenDialog(tokenName, () => {
        return deleteToken(id);
      });
    },
    [openRevokeTokenDialog, deleteToken]
  );

  return useMemo<ColumnDef<Token, any>[]>(
    () => [
      columnHelper.accessor('description', {
        header: () => (
          <span data-testid='token-name-col-header'>
            {t('app.dashboard.token_management.columns.token_name', {
              defaultValue: 'Token Name',
            })}
          </span>
        ),
        cell: ({ getValue }) => (
          <FlyTextOverflow
            data-testid='token-name-col'
            size='3'
            highContrast
            content={getValue()}
          >
            <Search text={getValue()} />
          </FlyTextOverflow>
        ),
        sortingFn: sortingFns.alphanumeric,
        enableSorting: true,
        sortDescFirst: true,
      }),
      columnHelper.accessor('expiry', {
        header: () => (
          <span data-testid='token-expiration-col-header'>
            {t('app.dashboard.token_management.columns.expiration_date', {
              defaultValue: 'Expiration Date',
            })}
          </span>
        ),
        cell: ({ getValue }) => {
          const expiry = getValue();

          if (isUnlimitedExpiration(expiry)) {
            return (
              <FlyTextOverflow
                data-testid='token-expired-col'
                size='3'
                highContrast
              >
                {t(
                  'app.dashboard.token_management.columns.expiration_time.unlimited',
                  { defaultValue: 'Unlimited' }
                )}
              </FlyTextOverflow>
            );
          }

          if (Date.now() > expiry.valueOf()) {
            return (
              <Badge variant='soft' color='neutral' size='1' className='w-fit'>
                {t('app.dashboard.token_management.columns.expired', {
                  defaultValue: 'Expired',
                })}
              </Badge>
            );
          }

          return (
            <FlyTextOverflow
              data-testid='token-expired-col'
              size='3'
              highContrast
            >
              {moment(expiry).format('MMM D, YYYY')}
            </FlyTextOverflow>
          );
        },
        sortingFn: (rowA, rowB, columnId) =>
          getExpiryTime(rowA.getValue<Date>(columnId)) -
          getExpiryTime(rowB.getValue<Date>(columnId)),
        enableSorting: true,
        sortDescFirst: true,
      }),
      columnHelper.accessor('scope', {
        header: () => (
          <span data-testid='token-permission-col-header'>
            {t('app.dashboard.token_management.columns.permission', {
              defaultValue: 'Permission',
            })}
          </span>
        ),
        cell: ({ getValue }) => (
          <FlyTextOverflow
            data-testid='token-permission-col'
            size='3'
            highContrast
          >
            {t(
              `app.dashboard.token_management.columns.permission_${getValue()}`,
              {
                defaultValue: getValue(),
              }
            )}
          </FlyTextOverflow>
        ),
        sortingFn: sortingFns.alphanumeric,
        enableSorting: true,
        sortDescFirst: true,
      }),
      columnHelper.accessor('issuedAt', {
        header: () => (
          <span data-testid='token-creation-col-header'>
            {t('app.dashboard.token_management.columns.creation_date', {
              defaultValue: 'Creation Date',
            })}
          </span>
        ),
        cell: ({ getValue }) => (
          <FlyTextOverflow
            data-testid='token-creation-col'
            size='3'
            highContrast
          >
            {moment(getValue()).format('MMM D, YYYY')}
          </FlyTextOverflow>
        ),
        sortingFn: sortingFns.datetime,
        enableSorting: true,
        sortDescFirst: true,
      }),
      columnHelper.accessor('subject', {
        header: () => (
          <span data-testid='token-email-col-header'>
            {t('app.dashboard.token_management.columns.token_username', {
              defaultValue: 'Token Username',
            })}
          </span>
        ),
        cell: ({ getValue }) => (
          <FlyTextOverflow
            className='fs-mask'
            data-testid='token-email-col'
            size='3'
            highContrast
          >
            <span>{getValue()}</span>
          </FlyTextOverflow>
        ),
        sortingFn: sortingFns.alphanumeric,
        enableSorting: true,
        sortDescFirst: true,
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        minSize: 90,
        size: 90,
        cell: ({ row }) =>
          canRevokeToken(
            row.original.expiry,
            isAdmin,
            row.original.issuer,
            row.original.subject
          ) ? (
            <Button
              data-testid='token-revoke-col-btn'
              size='2'
              variant='surface'
              color='error'
              className='ml-auto'
              onClick={() =>
                handleRevokeToken(
                  row.original.description,
                  row.original.tokenId
                )
              }
            >
              {t('app.dashboard.token_management.buttons.revoke', {
                defaultValue: 'Revoke',
              })}
            </Button>
          ) : null,
      }),
    ],
    [t, isAdmin, handleRevokeToken, FlyTextOverflow, Search]
  );
}
